<template>
  <div class="campaign-list">
    <div class="campaign-list__header">
      <div>
        <h1 class="page-title">Campañas de Correo</h1>
        <p class="page-subtitle">
          Gestiona las campañas de correo automático para clientes
        </p>
      </div>
      <div class="campaign-list__actions">
        <b-button
          v-if="usuarioPuedeExportar"
          variant="outline"
          class="exportar-btn mr-2"
          :disabled="exportando"
          @click="exportarCampanas"
        >
          <b-spinner v-if="exportando" small class="mr-1"></b-spinner>
          <b-icon v-else icon="file-earmark-arrow-down-fill" class="mr-1"></b-icon>
          {{ exportando ? 'Exportando...' : 'Exportar a Excel' }}
        </b-button>
        <b-button
          variant="primary"
          @click="$router.push({ name: 'campaign-create' })"
        >
          <i class="fas fa-plus mr-2"></i>
          Crear Nueva Campaña
        </b-button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-section">
      <b-row>
        <b-col md="4">
          <b-form-group label="Tipo de OT">
            <b-form-select v-model="filtroTipoOT" :options="opcionesFiltroTipoOT" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Estado">
            <b-form-select v-model="filtroEstado" :options="opcionesFiltroEstado" />
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando campañas...</p>
    </div>

    <!-- Error -->
    <b-alert v-if="error" show variant="danger" dismissible @dismissed="clearError">
      {{ error }}
    </b-alert>

    <!-- Tabla de campañas -->
    <div v-if="!loading" class="table-container">
      <b-table
        :items="campanasFiltradas"
        :fields="fields"
        hover
        :per-page="perPage"
        :current-page="currentPage"
        show-empty
        empty-text="No hay campañas configuradas"
        class="campanas-table"
        thead-class="table-header"
      >
        <!-- Columna: Nombre -->
        <template #cell(nombre)="data">
          <span class="campaign-name">{{ data.item.nombre }}</span>
        </template>

        <!-- Columna: Regla de Envío -->
        <template #cell(regla)="data">
          <span class="rule-text">{{ formatearRegla(data.item) }}</span>
        </template>

        <!-- Columna: Tipo OT -->
        <template #cell(tipo_ot)="data">
          <span :class="['tipo-ot-badge', 'badge', data.item.tipo_ot === 'GARANTIA' ? 'badge-garantia' : 'badge-presupuesto']">
            <font-awesome-icon
              v-if="data.item.tipo_ot === 'GARANTIA'"
              :icon="['fas', 'shield-alt']"
              class="badge-icon"
            />
            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" class="badge-icon">
              <path
                d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20
                21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                fill="currentColor"
              />
            </svg>
            {{ getTipoOTLabel(data.item.tipo_ot) }}
          </span>
        </template>

        <!-- Columna: Enviados -->
        <template #cell(enviados)="data">
          <span class="stat-value">{{ data.item.enviados || 0 }}</span>
        </template>

        <!-- Columna: Apertura % -->
        <template #cell(apertura)="data">
          <span :class="['stat-value', getAperturaClass(data.item.apertura)]">
            {{ data.item.apertura != null ? data.item.apertura + '%' : '0%' }}
          </span>
        </template>

        <!-- Columna: Clics % -->
        <template #cell(clics)="data">
          <span class="stat-value">
            {{ data.item.clics != null ? data.item.clics + '%' : '0%' }}
          </span>
        </template>

        <!-- Columna: Rebote % -->
        <template #cell(rebote)="data">
          <span class="stat-value">
            {{ data.item.rebote != null ? data.item.rebote + '%' : '0%' }}
          </span>
        </template>

        <!-- Columna: Estado (Toggle) -->
        <template #cell(activo)="data">
          <b-form-checkbox
            :checked="data.item.activo"
            switch
            size="lg"
            class="estado-toggle"
            @change="handleToggleActivo(data.item.id)"
          />
        </template>

        <!-- Columna: Acciones -->
        <template #cell(acciones)="data">
          <b-dropdown
            right
            variant="warning"
            size="sm"
            class="acciones-dropdown"
            toggle-class="acciones-btn"
            no-caret
          >
            <template #button-content>
              <font-awesome-icon :icon="['fas', 'cog']" class="mr-1" />
              Acciones
            </template>
            <b-dropdown-item @click="verMetricas(data.item.id)">
              <font-awesome-icon :icon="['fas', 'chart-bar']" class="mr-2" /> Ver Metricas
            </b-dropdown-item>
            <b-dropdown-item @click="editarCampana(data.item.id)">
              <font-awesome-icon :icon="['fas', 'edit']" class="mr-2" /> Editar
            </b-dropdown-item>
            <b-dropdown-item @click="enviarPrueba(data.item)">
              <font-awesome-icon :icon="['fas', 'paper-plane']" class="mr-2" /> Enviar Prueba
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item variant="danger" @click="confirmarEliminacion(data.item)">
              <font-awesome-icon :icon="['fas', 'trash']" class="mr-2" /> Eliminar
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Paginación -->
      <b-pagination
        v-if="totalRows > perPage"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        class="mt-3"
      ></b-pagination>
    </div>

    <!-- Modal de confirmación de eliminación -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirmar Eliminación"
      @ok="doEliminarCampana"
      ok-title="Eliminar"
      ok-variant="danger"
      cancel-title="Cancelar"
    >
      <p>
        ¿Estás seguro de que deseas eliminar la campaña
        <strong>{{ campanaAEliminar && campanaAEliminar.nombre }}</strong>?
      </p>
      <p class="text-muted mb-0">Esta acción no se puede deshacer.</p>
    </b-modal>

    <!-- Modal de Envío de Prueba -->
    <b-modal
      v-model="showTestModal"
      title="Enviar Correo de Prueba"
      :ok-disabled="sendingTest"
      no-close-on-backdrop
    >
      <div v-if="selectedCampaign">
        <p class="mb-3 modal-campaign-name">
          {{ selectedCampaign.nombre }}
        </p>

        <!-- Seleccionar Variante -->
        <b-form-group label="Seleccionar Variante" label-for="select-variante">
          <b-form-select
            id="select-variante"
            v-model="selectedVariant"
            :disabled="sendingTest"
          >
            <b-form-select-option :value="null">Seleccione</b-form-select-option>
            <b-form-select-option
              v-for="variante in selectedCampaign.variantes"
              :key="variante.id"
              :value="variante.id"
            >
              Variante {{ variante.variant_number }} - {{ variante.asunto || 'Sin asunto' }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>

        <!-- Enviar a -->
        <b-form-group label="Enviar a" label-for="input-email">
          <div class="email-input-container" @click="focusEmailInput">
            <!-- Badges dentro del input -->
            <b-badge
              v-for="(email, index) in emails"
              :key="index"
              variant="secondary"
              pill
              class="email-badge-inline mr-1 mb-1"
            >
              {{ email }}
              <span
                class="badge-close-inline"
                @click.stop="removerEmail(index)"
              >
                ×
              </span>
            </b-badge>

            <!-- Input inline -->
            <input
              ref="emailInput"
              v-model="emailInput"
              type="text"
              class="email-input-inline"
              placeholder="ej: ejemplo@samtek.cl"
              :disabled="sendingTest"
              @keydown.enter.prevent="agregarEmail"
              @keydown.space.prevent="agregarEmail"
              @keydown.tab.prevent="agregarEmailYAvanzar"
              @keydown.backspace="handleBackspace"
            />
          </div>
        </b-form-group>

        <!-- Mensaje de ayuda -->
        <small class="text-muted d-block mb-3">
          Presiona Enter, Espacio o Tab para agregar un email
        </small>

        <!-- Loading -->
        <div v-if="sendingTest" class="text-center mt-3">
          <b-spinner small variant="primary" />
          <span class="ml-2">Enviando correos...</span>
        </div>
      </div>

      <!-- Footer personalizado -->
      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <b-button
            class="btn-cancelar-custom"
            :disabled="sendingTest"
            @click="showTestModal = false"
          >
            Cancelar
          </b-button>
          <b-button
            variant="warning"
            class="btn-aceptar-custom"
            :disabled="sendingTest"
            @click="enviarCorreosPrueba"
          >
            Aceptar
          </b-button>
        </div>
      </template>
    </b-modal>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { TIPO_OT_CHOICES, FECHA_REFERENCIA_CHOICES } from '@/utils/campaignValidation';
import campaignService from '@/services/campaignService';

export default {
  name: 'CampaignList',
  data() {
    return {
      fields: [
        { key: 'nombre', label: 'Nombre de la Campaña', sortable: true, thClass: 'th-nombre' },
        { key: 'regla', label: 'Regla de Envío', sortable: false },
        { key: 'tipo_ot', label: 'Tipo de OT', sortable: true },
        { key: 'enviados', label: 'Enviados', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'apertura', label: 'Apertura %', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'clics', label: 'Clics %', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'rebote', label: 'Rebote %', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'activo', label: 'Estado', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'acciones', label: 'Acciones', sortable: false, thClass: 'text-center', tdClass: 'text-center' }
      ],
      currentPage: 1,
      perPage: 10,
      filtroTipoOT: null,
      filtroEstado: null,
      opcionesFiltroTipoOT: [
        { value: null, text: 'Todos' },
        ...TIPO_OT_CHOICES.map(opt => ({ value: opt.value, text: opt.label }))
      ],
      opcionesFiltroEstado: [
        { value: null, text: 'Todos' },
        { value: true, text: 'Activos' },
        { value: false, text: 'Inactivos' }
      ],
      showDeleteModal: false,
      campanaAEliminar: null,
      showTestModal: false,
      selectedCampaign: null,
      selectedVariant: null,
      emailInput: '',
      emails: [],
      sendingTest: false,
      exportando: false
    };
  },
  computed: {
    ...mapState('Campaign', ['campanas', 'loading', 'error']),
    usuarioPuedeExportar() {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const user = storedUser.user || {};
        const groups = Array.isArray(user.groups) ? user.groups : [];

        return Boolean(
          user.is_superuser ||
          groups.some((group) => ['Administrativo', 'Gerencia'].includes(group.name))
        );
      } catch (error) {
        return false;
      }
    },
    campanasFiltradas() {
      let filtered = [...this.campanas];

      if (this.filtroTipoOT) {
        filtered = filtered.filter(c => c.tipo_ot === this.filtroTipoOT);
      }

      if (this.filtroEstado !== null) {
        filtered = filtered.filter(c => c.activo === this.filtroEstado);
      }

      return filtered;
    },
    totalRows() {
      return this.campanasFiltradas.length;
    }
  },
  mounted() {
    this.cargarCampanas();
  },
  methods: {
    ...mapActions('Campaign', ['fetchCampanas', 'toggleActivo', 'eliminarCampana', 'clearError']),
    async cargarCampanas() {
      try {
        await this.fetchCampanas();
      } catch (error) {
        // Error manejado por el store
      }
    },
    async exportarCampanas() {
      const { key } = JSON.parse(localStorage.getItem('user') || '{}');

      if (!key) {
        this.$bvToast.toast('Debes iniciar sesión para exportar campañas', {
          title: 'Sesión requerida',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.exportando = true;

      try {
        const response = await campaignService.exportarCampanas();
        const blob = new Blob([response.data], { type: 'application/ms-excel' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', 'campanas_marketing.xls');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('La exportación se descargó correctamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const status = error.response?.status;
        const message = status === 403
          ? 'No tienes permisos para exportar campañas'
          : status === 401
            ? 'Tu sesión expiró. Inicia sesión nuevamente para exportar campañas'
            : 'No fue posible exportar las campañas. Intenta nuevamente';

        this.$bvToast.toast(message, {
          title: 'Error al exportar',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.exportando = false;
      }
    },
    getTipoOTLabel(tipo) {
      const opcion = TIPO_OT_CHOICES.find(opt => opt.value === tipo);
      return opcion ? opcion.label : tipo;
    },
    formatearRegla(campana) {
      const fechaOpt = FECHA_REFERENCIA_CHOICES.find(
        opt => opt.value === campana.fecha_referencia
      );
      const fechaLabel = (fechaOpt && fechaOpt.label) || campana.fecha_referencia;

      const operacionLabel = campana.operacion === 'SUMA' ? 'después' : 'antes';

      return `${campana.dias} día(s) ${operacionLabel} de ${fechaLabel}`;
    },
    editarCampana(id) {
      this.$router.push({ name: 'campaign-edit', params: { id } });
    },
    verMetricas(id) {
      this.$router.push({ name: 'campaign-metrics', params: { id } });
    },
    enviarPrueba(campana) {
      this.selectedCampaign = campana;
      this.selectedVariant = null;
      this.emailInput = '';
      this.emails = [];
      this.showTestModal = true;
    },
    validarEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    },
    agregarEmail() {
      const email = this.emailInput.trim();
      if (!email) return;

      if (!this.validarEmail(email)) {
        this.$bvToast.toast('Por favor ingresa un correo electrónico válido', {
          title: 'Email inválido',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (this.emails.includes(email)) {
        this.$bvToast.toast('Este correo ya está en la lista', {
          title: 'Email duplicado',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.emails.push(email);
      this.emailInput = '';
    },
    agregarEmailYAvanzar() {
      // Agregar email y mantener el foco
      this.agregarEmail();
    },
    handleBackspace(event) {
      // Si el input está vacío y hay emails, eliminar el último
      if (this.emailInput === '' && this.emails.length > 0) {
        event.preventDefault();
        this.emails.pop();
      }
    },
    focusEmailInput() {
      if (this.$refs.emailInput) {
        this.$refs.emailInput.focus();
      }
    },
    removerEmail(index) {
      this.emails.splice(index, 1);
    },
    async enviarCorreosPrueba() {
      if (!this.selectedVariant) {
        this.$bvToast.toast('Por favor selecciona una variante', {
          title: 'Variante requerida',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (this.emails.length === 0) {
        this.$bvToast.toast('Por favor agrega al menos un correo electrónico', {
          title: 'Email requerido',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.sendingTest = true;

      try {
        const variante = this.selectedCampaign.variantes.find(v => v.id === this.selectedVariant);

        // Enviar correo a cada destinatario
        const promesas = this.emails.map(email => {
          return campaignService.enviarCorreoPrueba({
            email_destinatario: email,
            asunto: variante.asunto,
            cuerpo: variante.cuerpo,
            variante_id: this.selectedVariant
          });
        });

        await Promise.all(promesas);

        this.$bvToast.toast(
          `Correo(s) de prueba enviado(s) exitosamente a ${this.emails.length} destinatario(s)`,
          {
            title: 'Éxito',
            variant: 'success',
            solid: true
          }
        );

        this.showTestModal = false;
      } catch (error) {
        this.$bvToast.toast('Error al enviar correo(s) de prueba', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.sendingTest = false;
      }
    },
    confirmarEliminacion(campana) {
      this.campanaAEliminar = campana;
      this.showDeleteModal = true;
    },
    async doEliminarCampana() {
      try {
        await this.$store.dispatch('Campaign/eliminarCampana', this.campanaAEliminar.id);
        this.$bvToast.toast('Campaña eliminada correctamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast('Error al eliminar la campaña', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.showDeleteModal = false;
        this.campanaAEliminar = null;
      }
    },
    async handleToggleActivo(id) {
      try {
        await this.$store.dispatch('Campaign/toggleActivo', id);
        this.$bvToast.toast('Estado actualizado correctamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast('Error al actualizar el estado', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    getAperturaClass(tasa) {
      if (tasa >= 50) return 'text-green';
      if (tasa >= 25) return 'text-warning';
      return 'text-danger';
    },
    getReboteClass(tasa) {
      if (tasa >= 30) return 'text-danger';
      if (tasa >= 10) return 'text-warning';
      return 'text-green';
    }
  }
};
</script>

<style scoped>
.exportar-btn {
  color: #23201c;
  background-color: #f5f6fa;
  border: solid #fece2f 1px;
  border-radius: 8px;
}
.exportar-btn:hover {
  color: black;
  background-color: rgba(254, 206, 47, 0.1);
  border-color: #e5b823;
  box-shadow: none;
}
.text-green {
  color: rgba(23, 219, 148, 1);
}
.campaign-list {
  padding: 24px;
  background: #F5F6FA;
  min-height: calc(100vh - 60px);
}

.campaign-list__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.campaign-list__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.filters-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.table-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: visible;
}

::v-deep .table-responsive {
  overflow: visible;
}

/* Tabla */
::v-deep .campanas-table {
  font-size: 14px;
  color: #1F2937;
}

::v-deep .campanas-table .table-header th {
  background: #DDDDE1;
  color: #374151;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 2px solid #E5E7EB;
  padding: 12px 16px;
  white-space: nowrap;
}

::v-deep .campanas-table tbody tr td {
  padding: 14px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #F3F4F6;
}

::v-deep .campanas-table tbody tr:hover td {
  background: #FAFAFA;
}

.campaign-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.rule-text {
  font-size: 13px;
  color: #6B7280;
}

/* Badges tipo OT */
.tipo-ot-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-presupuesto {
  background: #DBEAFE;
  color: #1D4ED8;
}

.badge-garantia {
  background: #D1FAE5;
  color: #065F46;
}

.badge-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

/* Stats */
.stat-value {
  font-size: 14px;
  font-weight: 00;
}

.action-dropdown {
  min-width: 120px;
}

.action-dropdown .btn {
  font-weight: 500;
  color: #374151;
}

.apertura-alta {
  color: #D97706;
  font-weight: 700;
}

/* Toggle */
::v-deep .estado-toggle .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #17DB94 !important;
  border-color: #17DB94 !important;
}

::v-deep .estado-toggle .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(23, 219, 148, 0.25) !important;
}

::v-deep .estado-toggle .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
  border-color: #adb5bd !important;
}

/* Acciones dropdown */
::v-deep .acciones-dropdown .acciones-btn {
  background: #FECE2F;
  border-color: #FECE2F;
  color: #23201C;
  font-weight: 600;
  font-size: 13px;
  padding: 5px 12px;
  border-radius: 6px;
}

::v-deep .acciones-dropdown .acciones-btn:hover,
::v-deep .acciones-dropdown .acciones-btn:focus {
  background: #f5c400;
  border-color: #f5c400;
  color: #23201C;
  box-shadow: none;
}

::v-deep .acciones-dropdown .dropdown-menu {
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 140px;
}

/* Modal de Envío de Prueba */
.modal-campaign-name {
  color: #525252;
  font-weight: 600;
}
.email-input-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  min-height: 42px;
  cursor: text;
}

.email-input-container:focus-within {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.email-input-inline {
  flex: 1;
  min-width: 200px;
  border: none;
  outline: none;
  padding: 0;
  font-size: 14px;
}

.email-input-inline::placeholder {
  color: #6c757d;
}

.email-badge-inline {
  font-size: 13px;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  background-color: #6c757d !important;
  margin: 0;
}

.badge-close-inline {
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  font-weight: 600;
  margin-left: 4px;
  opacity: 0.7;
}

.badge-close-inline:hover {
  opacity: 1;
}

.emails-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.email-badge {
  font-size: 14px;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  background-color: #6c757d !important;
}

.cursor-pointer {
  cursor: pointer;
}

.badge-close {
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  font-weight: 600;
  margin-left: 6px;
}

.badge-close:hover {
  opacity: 0.7;
}

/* Botón Cancelar Personalizado */
.btn-cancelar-custom {
  display: flex !important;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 20px !important;
  min-width: 110px;
  height: 45px !important;
  background: #F5F6FA !important;
  border-radius: 8px !important;
  border: none !important;
  color: #000 !important;
  font-weight: 500;
  transition: background 0.2s ease;
}

.btn-cancelar-custom:hover {
  background: #DDDDE1 !important;
  color: #000 !important;
}

.btn-cancelar-custom:focus {
  background: #DDDDE1 !important;
  box-shadow: none !important;
  color: #000 !important;
}

.btn-cancelar-custom:disabled {
  background: #F5F6FA !important;
  opacity: 0.6;
  cursor: not-allowed;
}

/* Botón Aceptar Personalizado */
.btn-aceptar-custom {
  display: flex !important;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 20px !important;
  min-width: 110px;
  height: 45px !important;
  border-radius: 8px !important;
  font-weight: 500;
}
</style>
